import { useEffect, useState } from "react";
import { Switch } from "@/components/ui/switch";
import { api, errorMessage, formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isWidgetLoaded, removeWidgetAndReload } from "@/lib/widget";
import { Notice } from "@/components/form/FormFeedback";

export const PrivacyTab = ({ company, onChange }) => {
    const { refresh } = useAuth();
    const [consent, setConsent] = useState(company.ai_consent || { given: false });
    const [notice, setNotice] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/companies/me/consent").then(({ data }) => {
            setConsent(data.ai_consent);
            setNotice(data.notice);
        }).catch(() => {});
    }, []);

    const toggle = async (given) => {
        const previous = consent;
        setConsent({ ...consent, given });
        setError("");
        setBusy(true);
        try {
            const { data } = await api.put("/companies/me/consent", { given });
            setConsent(data.ai_consent);
            onChange({ ...company, ai_consent: data.ai_consent });
            refresh();
            if (!given && isWidgetLoaded()) removeWidgetAndReload();
        } catch {
            setConsent(previous);
            setError("Impossibile salvare il consenso, riprova");
        } finally {
            setBusy(false);
        }
    };

    return (
        <section aria-labelledby="privacy-title" className="edi-card p-5 sm:p-6 max-w-3xl">
            <h2 id="privacy-title" className="text-xl sm:text-2xl font-bold">Privacy e consensi</h2>
            <h3 className="mt-6 text-lg font-bold">Informativa sull'assistente AI</h3>
            <p className="mt-2 text-edi-muted whitespace-pre-line" data-testid="ai-privacy-notice">{notice}</p>
            <div className="mt-6 rounded-lg border border-edi-border p-4 flex items-start gap-4">
                <Switch
                    id="ai-consent-switch"
                    checked={Boolean(consent?.given)}
                    onCheckedChange={toggle}
                    disabled={busy}
                    aria-describedby="ai-consent-status"
                    data-testid="ai-consent-switch"
                    className="mt-1 h-7 w-12 border-2 border-transparent rounded-lg data-[state=checked]:bg-edi-lime data-[state=unchecked]:bg-edi-track [&>span]:h-5 [&>span]:w-5 [&>span]:data-[state=checked]:translate-x-5"
                />
                <div>
                    <label htmlFor="ai-consent-switch" className="font-medium cursor-pointer">Acconsento al trattamento dei dati per l'utilizzo dell'assistente AI</label>
                    <p id="ai-consent-status" role="status" aria-live="polite" className="mt-1 font-semibold" data-testid="ai-consent-status">
                        {consent?.given ? `Consenso espresso il ${formatDate(consent.updated_at)}` : "Consenso non espresso"}
                    </p>
                </div>
            </div>
            {error && <div className="mt-4"><Notice kind="error" role="alert" testId="ai-consent-error">{error}</Notice></div>}
            {company.privacy_accepted_at && (
                <p className="mt-6 text-sm text-edi-muted" data-testid="registration-privacy-date">
                    Informativa privacy di registrazione accettata il {formatDate(company.privacy_accepted_at)}.
                </p>
            )}
        </section>
    );
};

export { errorMessage };
